import { useState, useEffect } from 'react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Logo from '../components/common/Logo';
import { COUNTIES, GENDER_OPTIONS, MARITAL_STATUS_OPTIONS, EDUCATION_LEVELS, EMPLOYMENT_STATUS_OPTIONS, LOAN_PURPOSES } from '../utils/constants';
import { validateMpesaNumber, normalizeMpesaNumber } from '../utils/validators';
import { getUserProfile, saveProfileStep1, saveProfileStep2 } from '../services/firestore';
import useAuthStore from '../stores/authStore';

const Profile = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    county: '',
    mpesaNumber: '',
    nationalId: '',
    gender: '',
    dateOfBirth: '',
    maritalStatus: '',
    education: '',
    employmentStatus: '',
    monthlyIncome: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { user, profile, setProfile } = useAuthStore();

  useEffect(() => {
    const loadProfile = async () => {
      if (user?.uid) {
        setLoading(true);
        try {
          const result = await getUserProfile(user.uid);
          if (result.success) {
            setFormData(prev => ({
              ...prev,
              ...result.profile
            }));
          }
        } catch (error) {
          console.error('Load profile error:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadProfile();
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.county) {
      newErrors.county = 'County is required';
    }

    if (!formData.mpesaNumber.trim()) {
      newErrors.mpesaNumber = 'M-Pesa number is required';
    } else {
      // Remove spaces for validation
      const cleanNumber = formData.mpesaNumber.replace(/\s/g, '');
      if (!validateMpesaNumber(cleanNumber)) {
        newErrors.mpesaNumber = 'Please enter a valid M-Pesa number (07XXXXXXXX or 01XXXXXXXX)';
      }
    }

    if (!formData.nationalId.trim()) {
      newErrors.nationalId = 'National ID is required';
    } else if (!/^\d{8}$/.test(formData.nationalId)) {
      newErrors.nationalId = 'National ID must be 8 digits';
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      if (age < 18) {
        newErrors.dateOfBirth = 'You must be at least 18 years old';
      }
    }

    if (!formData.maritalStatus) {
      newErrors.maritalStatus = 'Marital status is required';
    }

    if (!formData.education) {
      newErrors.education = 'Education level is required';
    }

    if (!formData.employmentStatus) {
      newErrors.employmentStatus = 'Employment status is required';
    }

    if (!formData.monthlyIncome.trim()) {
      newErrors.monthlyIncome = 'Monthly income is required';
    } else if (isNaN(formData.monthlyIncome) || Number(formData.monthlyIncome) < 0) {
      newErrors.monthlyIncome = 'Please enter a valid income amount';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setSaving(true);
    
    try {
      // Keep M-Pesa number in local format - no normalization
      
      // Save step 1 data
      const step1Data = {
        fullName: formData.fullName,
        email: formData.email,
        county: formData.county,
        mpesaNumber: formData.mpesaNumber.replace(/\s/g, ''), // Just remove spaces
        nationalId: formData.nationalId,
        gender: formData.gender
      };

      const step1Result = await saveProfileStep1(user.uid, step1Data);
      
      if (!step1Result.success) {
        setErrors({ general: step1Result.error });
        return;
      }

      // Save step 2 data
      const step2Data = {
        dateOfBirth: formData.dateOfBirth,
        maritalStatus: formData.maritalStatus,
        education: formData.education,
        employmentStatus: formData.employmentStatus,
        monthlyIncome: Number(formData.monthlyIncome)
      };

      const step2Result = await saveProfileStep2(user.uid, step2Data);
      
      if (step2Result.success) {
        // Update local profile state
        const updatedProfile = { 
          ...formData, 
          mpesaNumber: formData.mpesaNumber.replace(/\s/g, ''), // Remove spaces
          profileCompleted: true 
        };
        setProfile(updatedProfile);
        
        // Show success message
        alert('Profile updated successfully!');
      } else {
        setErrors({ general: step2Result.error });
      }
    } catch (error) {
      console.error('Save profile error:', error);
      setErrors({ general: 'An error occurred. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="form-container">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <Logo size="xl" className="mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Profile Settings
            </h1>
            <p className="text-gray-600">
              Update your personal information
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Full Name"
                  name="fullName"
                  placeholder="Enter your full name"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  error={errors.fullName}
                  required
                />

                <Input
                  label="Email Address"
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={errors.email}
                  required
                  disabled
                />

                <Select
                  label="County"
                  name="county"
                  options={COUNTIES}
                  value={formData.county}
                  onChange={handleInputChange}
                  error={errors.county}
                  placeholder="Select your county"
                  required
                />

                <Select
                  label="Gender"
                  name="gender"
                  options={GENDER_OPTIONS}
                  value={formData.gender}
                  onChange={handleInputChange}
                  error={errors.gender}
                  placeholder="Select your gender"
                  required
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="M-Pesa Number"
                  type="tel"
                  name="mpesaNumber"
                  placeholder="07X XXX XXXX"
                  value={formData.mpesaNumber}
                  onChange={handleInputChange}
                  error={errors.mpesaNumber}
                  required
                />

                <Input
                  label="National ID Number"
                  type="tel"
                  name="nationalId"
                  placeholder="Enter 8-digit National ID"
                  value={formData.nationalId}
                  onChange={handleInputChange}
                  error={errors.nationalId}
                  maxLength={8}
                  required
                />
              </div>
            </div>

            {/* Personal Details */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Date of Birth"
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  error={errors.dateOfBirth}
                  required
                />

                <Select
                  label="Marital Status"
                  name="maritalStatus"
                  options={MARITAL_STATUS_OPTIONS}
                  value={formData.maritalStatus}
                  onChange={handleInputChange}
                  error={errors.maritalStatus}
                  placeholder="Select marital status"
                  required
                />

                <Select
                  label="Level of Education"
                  name="education"
                  options={EDUCATION_LEVELS}
                  value={formData.education}
                  onChange={handleInputChange}
                  error={errors.education}
                  placeholder="Select education level"
                  required
                />

                <Select
                  label="Employment Status"
                  name="employmentStatus"
                  options={EMPLOYMENT_STATUS_OPTIONS}
                  value={formData.employmentStatus}
                  onChange={handleInputChange}
                  error={errors.employmentStatus}
                  placeholder="Select employment status"
                  required
                />
              </div>
            </div>

            {/* Financial Information */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Information</h3>
              <Input
                label="Monthly Income (KES)"
                type="number"
                name="monthlyIncome"
                placeholder="Enter your monthly income"
                value={formData.monthlyIncome}
                onChange={handleInputChange}
                error={errors.monthlyIncome}
                min="0"
                required
              />
            </div>

            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <span className="text-red-700 text-sm">{errors.general}</span>
                </div>
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={saving}
            >
              {saving ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </div>
              ) : (
                'Update Profile'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;